import React, { useState } from 'react';
import { Modal, Button, Input } from '@/components/ui';
import { JsonViewer } from './JsonViewer';
import { classNames } from '@/lib/utils';

export interface ColumnConfig {
  key: string;
  label: string;
  format?: (value: any, row: any) => React.ReactNode;
  align?: 'left' | 'center' | 'right';
  isMonospace?: boolean;
  isStatus?: boolean;
}

interface GenericDataTableProps {
  data: any[];
  columns: ColumnConfig[];
  onDelete?: (id: string) => Promise<void>;
  disableRowClick?: boolean;
}

function StatusIndicator({ value }: { value: string }) {
  const status = String(value).toLowerCase();

  const isPulse = status === 'settling' || status === 'active';
  const dotColor =
    status === 'settling' ? 'bg-amber-500' :
    status === 'settled' || status === 'completed' || status === 'active' ? 'bg-emerald-500' :
    status === 'failed' || status === 'error' ? 'bg-red-500' :
    'bg-[#6b7280]/20';

  return (
    <div className="flex items-center gap-2.5">
      <div className="relative flex h-2 w-2">
        {isPulse && (
          <span className={classNames("animate-ping absolute inline-flex h-full w-full rounded-full opacity-75", dotColor)}></span>
        )}
        <span className={classNames("relative inline-flex rounded-full h-2 w-2", dotColor)}></span>
      </div>
      <span className="text-sm font-bold capitalize text-[#2c2f30]/80">{status}</span>
    </div>
  );
}

export function GenericDataTable({ data, columns, onDelete, disableRowClick }: GenericDataTableProps) {
  const [selectedDoc, setSelectedDoc] = useState<any | null>(null);
  const [docToDelete, setDocToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  if (data.length === 0) {
    return (
      <div className="py-32 flex flex-col items-center justify-center text-center">
        <span className="material-symbols-outlined text-[#6b7280]/10 text-7xl mb-6">database_off</span>
        <div className="text-[10px] font-black uppercase tracking-[3px] text-[#6b7280]/50">No records found</div>
      </div>
    );
  }

  const handleCloseModal = () => {
    setSelectedDoc(null);
    setSearchTerm('');
  };

  const copyId = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse min-w-[800px]">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="pl-6 pr-4 py-4 text-[10px] font-black uppercase tracking-[2px] text-[#6b7280] whitespace-nowrap">
                ID
              </th>
              {columns.map(col => (
                <th
                  key={col.key}
                  className={classNames(
                    "px-4 py-4 text-[10px] font-black uppercase tracking-[2px] text-[#6b7280] whitespace-nowrap",
                    col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
                  )}
                >
                  {col.label}
                </th>
              ))}
              <th className="pl-4 pr-6 py-4 text-[10px] font-black uppercase tracking-[2px] text-[#6b7280] text-right">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {data.map((row, idx) => (
              <tr
                key={row.id || idx}
                className="group hover:bg-[#f5f7f8] cursor-pointer transition-all duration-200"
                onClick={disableRowClick ? undefined : () => setSelectedDoc(row)}
              >
                <td className="pl-6 pr-4 py-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono font-medium text-[#6b7280] group-hover:text-[#006855] transition-colors">
                      {row.id?.substring(0, 8)}
                    </span>
                    <button
                      onClick={(e) => copyId(row.id, e)}
                      className="p-1 rounded hover:bg-[#006855]/10 text-[#6b7280]/20 hover:text-[#006855] transition-all opacity-0 group-hover:opacity-100"
                      title="Copy full ID"
                    >
                      <span className="material-symbols-outlined text-sm">
                        {copiedId === row.id ? 'check' : 'content_copy'}
                      </span>
                    </button>
                  </div>
                </td>
                {columns.map(col => (
                  <td
                    key={col.key}
                    className={classNames(
                      "px-4 py-4",
                      col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
                    )}
                  >
                    {col.isStatus ? (
                      <div className={classNames("inline-flex", col.align === 'center' && 'justify-center w-full')}>
                        <StatusIndicator value={row[col.key]} />
                      </div>
                    ) : (
                      <span className={classNames(
                        "text-sm font-semibold text-[#2c2f30]/70 group-hover:text-[#2c2f30] transition-colors",
                        col.isMonospace ? 'font-mono tracking-tight' : ''
                      )}>
                        {col.format ? col.format(row[col.key], row) : String(row[col.key] ?? '-')}
                      </span>
                    )}
                  </td>
                ))}
                <td className="pl-4 pr-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); setSelectedDoc(row); }}
                      className="p-2 rounded-full text-[#6b7280]/20 hover:text-[#006855] hover:bg-[#006855]/10 transition-all"
                      title="View Details"
                    >
                      <span className="material-symbols-outlined text-lg">visibility</span>
                    </button>
                    {onDelete && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setDocToDelete(row.id); }}
                        className="p-2 rounded-full text-[#6b7280]/20 hover:text-red-500 hover:bg-red-50 transition-all"
                        title="Delete Record"
                      >
                        <span className="material-symbols-outlined text-lg">delete</span>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedDoc && (
        <Modal 
          isOpen={!!selectedDoc} 
          onClose={handleCloseModal} 
          title="Document Inspection" 
          maxWidth="max-w-2xl"
        >
          <div className="space-y-6">
            <div className="flex items-center justify-between bg-[#f5f7f8] p-4 rounded-xl border border-gray-100">
              <div>
                <div className="text-[10px] font-black text-[#6b7280] uppercase tracking-widest mb-1">Full Document ID</div>
                <div className="text-xs font-mono font-bold text-[#006855] break-all">{selectedDoc.id}</div>
              </div>
              <Button variant="ghost" size="sm" onClick={(e) => copyId(selectedDoc.id, e)} className="bg-white border border-gray-200">
                <span className="material-symbols-outlined text-[16px]">content_copy</span>
              </Button>
            </div>

            <div className="space-y-3">
              <Input
                placeholder="Search JSON keys or values..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                label="Search Data"
                className="bg-white border border-gray-200"
              />
              <JsonViewer data={selectedDoc} searchTerm={searchTerm} />
            </div>
            
            <Button variant="secondary" fullWidth onClick={handleCloseModal}>Close Inspector</Button>
          </div>
        </Modal>
      )}

      {docToDelete && (
        <Modal 
          isOpen={true} 
          onClose={() => !isDeleting && setDocToDelete(null)} 
          title="Confirm Deletion"
        >
          <div className="space-y-6">
            <div className="p-4 bg-red-50 rounded-xl border border-red-100 flex gap-3">
              <span className="material-symbols-outlined text-red-500">warning</span>
              <p className="text-red-600 text-sm font-bold leading-relaxed">
                Irreversible Action: Deleting <code className="bg-red-100 px-1.5 py-0.5 rounded font-mono">{docToDelete}</code> will permanently remove it from the database.
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="ghost"
                onClick={() => setDocToDelete(null)}
                disabled={isDeleting}
                fullWidth
                className="bg-white border border-gray-200"
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={async () => {
                  if (!onDelete) return;
                  setIsDeleting(true);
                  try {
                    await onDelete(docToDelete);
                    setDocToDelete(null);
                  } catch (err) {
                    console.error("Delete failed", err);
                  } finally {
                    setIsDeleting(false);
                  }
                }}
                disabled={isDeleting}
                fullWidth
              >
                {isDeleting ? 'Deleting...' : 'Confirm Deletion'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
